import React from 'react';
import PropTypes from 'prop-types';
import { PayPalButtons, PayPalScriptProvider } from '@paypal/react-paypal-js';
import fetch from '../../../../utils/fetch.js';
import { DEFAULT_CURRENCY } from '../../../../utils/currency';

const debug = require('debug')('cw:donation:paypal');

const defaultStyle = {
  label: 'paypal',
  layout: 'horizontal',
  size: 'medium',
  shape: 'rect',
  color: 'blue',
  tagline: false,
};

const defaultCallback = () => {};

class PaypalButton extends React.Component {
  /**
   * Drupal Commerce Payment ID from the backend.
   */
  paymentID = 0;

  /**
   * Drupal Commerce Order ID from the backend.
   */
  orderID = 0;

  /**
   * Payment capture promise.
   * Legacy PayPal flow triggers payment() callback multiple times. We store
   * API calls in a promise to ensure they are executed only once.
   * See https://www.pivotaltracker.com/story/show/179450363.
   */
  paymentPromise = null;

  constructor(props) {
    super(props);

    this.onAuthorize = this.onAuthorize.bind(this);
    this.payment = this.payment.bind(this);
    this.onClick = this.onClick.bind(this);
  }

  /**
   * Triggered when a user confirms transaction in Paypal popup.
   * Sends request to finalize the payment to Drupal backend.
   */
  onAuthorize(data, actions) {
    this.paymentPromise = null;

    const { onSuccess = defaultCallback, onError = defaultCallback, backendURL } = this.props;

    return fetch(`${backendURL}/commerce/payment/capture/${this.orderID}/${this.paymentID}`, {
      method: 'POST',
      // Requests with 409 status code won't be sent to Rollbar and the request
      // won't trigger errors.
      ignoreStatusCodes: [409],
      headers: { 'Content-Type': 'application/json' },
      query: { _format: 'json' },
      body: data,
    })
      .then((response) => {
        debug('Payment execution response is %O.', response);
        onSuccess(this.orderID);
        return response;
      })
      .catch((error) => {
        debug(error);
        onError(error);
      });
  }

  /**
   * Triggers on Paypal button click.
   *
   * Paypal handler which should return Paypal token for payment
   * confirmation initialization. If the token is valid, Paypal will display
   * payment content inside of popup.
   *
   */
  async payment(data, actions) {
    const {
      onError = defaultCallback,
      onBeforeAuthentication = defaultCallback,
      isSubscription = false,
      backendURL,
      payload,
      paymentGateway,
      paymentType = 'paypal_ec',
    } = this.props;
    // Initialize payment promise only once to avoid duplicated API calls.
    if (!this.paymentPromise) {
      this.paymentPromise = onBeforeAuthentication()
        .then((orderID) => {
          this.orderID = orderID;

          return fetch(`${backendURL}/commerce/payment/create/${orderID}`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            query: { _format: 'json' },
            body: {
              gateway: paymentGateway,
              type: paymentType,
              details: {
                type: isSubscription ? 'subscription' : 'single',
                data: payload,
              },
            },
          });
        })
        .then((response) => {
          debug('Payment creation response is %O.', response);
          this.paymentID = response.body.payment_id[0].value;
          return response.body.remote_id[0].value;
        })
        .catch((error) => {
          debug(error);
          onError(error);
        });
    }

    return this.paymentPromise;
  }

  onClick = () => {
    // Clean up previously created payment promise.
    this.paymentPromise = null;
    this.props.onClick();
  };

  render() {
    const {
      env,
      client,
      style = defaultStyle,
      onError = defaultCallback,
      onCancel = defaultCallback,
      disabled = false,
      visible = true,
    } = this.props;

    const paypalOpts = {};

    if (this.props.isSubscription) {
      paypalOpts.createSubscription = this.payment;
    } else {
      paypalOpts.createOrder = this.payment;
    }

    return (
      <div className={`paypal-button-wrapper${visible === false ? ' paypal-preloading' : ''}`}>
        <PayPalScriptProvider
          options={{
            clientId: client[env],
            currency: DEFAULT_CURRENCY,
            vault: this.props.isSubscription,
            // Disable all available payment methods except standard paypal button.
            // https://developer.paypal.com/sdk/js/configuration/
            disableFunding:
              'credit,card,paylater,bancontact,blik,eps,giropay,ideal,mercadopago,mybank,p24,sepa,sofort,venmo',
          }}
        >
          <PayPalButtons
            style={style}
            onCancel={onCancel}
            onError={onError}
            onClick={this.onClick}
            onApprove={this.onAuthorize}
            disabled={disabled}
            {...paypalOpts}
          />
        </PayPalScriptProvider>
      </div>
    );
  }
}

PaypalButton.propTypes = {
  env: PropTypes.oneOf(['sandbox', 'production']).isRequired,
  client: PropTypes.shape({
    sandbox: PropTypes.string,
    production: PropTypes.string,
  }).isRequired,
  style: PropTypes.shape({
    label: PropTypes.oneOf(['checkout', 'credit', 'pay', 'buynow', 'generic', 'paypal']),
    layout: PropTypes.oneOf(['horizontal', 'vertical']),
    size: PropTypes.oneOf(['small', 'medium', 'large', 'responsive']),
    shape: PropTypes.oneOf(['pill', 'rect']),
    color: PropTypes.oneOf(['gold', 'blue', 'silver', 'white', 'black']),
    tagline: PropTypes.bool,
  }),
  onError: PropTypes.func,
  onCancel: PropTypes.func,
  onSuccess: PropTypes.func,
  onClick: PropTypes.func,
  onBeforeAuthentication: PropTypes.func,
  isSubscription: PropTypes.bool,
  paymentGateway: PropTypes.string.isRequired,
  paymentType: PropTypes.string,
  backendURL: PropTypes.string.isRequired,
  payload: PropTypes.shape().isRequired,
  visible: PropTypes.bool,
  disabled: PropTypes.bool,
};

export default PaypalButton;
